import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { PlatformMethod } from './platform-methods';
import { StatusDTO } from 'src/app/maui/models/status-dto';

export enum PlatformType {
  'WEB' = 'WEB',
  'ELECTRON' = 'ELECTRON',
  'MAUI' = 'MAUI'
}

@Injectable({
  providedIn: 'root'
})
export class PlatformService {
  protected _platform: PlatformType = PlatformType.WEB;
  protected _platformInitialized: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public platformInitialized$: Observable<boolean> = this._platformInitialized.asObservable();

  protected _backgroundSessionStarted: Subject<boolean> = new Subject();
  public backgroundSessionStarted$: Observable<boolean> = this._backgroundSessionStarted.asObservable();

  protected _initialIndexDownloadStatusReceived: Subject<Uint8Array> = new Subject();
  public initialIndexDownloadStatusReceived$: Observable<Uint8Array> = this._initialIndexDownloadStatusReceived.asObservable();

  protected _initialZipDownloadStatusReceived: Subject<number> = new Subject();
  public initialZipDownloadStatusReceived$: Observable<number> = this._initialZipDownloadStatusReceived.asObservable();

  protected _downloadFileStatusReceived: Subject<StatusDTO> = new Subject();
  public downloadFileStatusReceived$: Observable<StatusDTO> = this._downloadFileStatusReceived.asObservable();

  public get platform(): PlatformType {
    return this._platform;
  }

  public isOfflineCapable(): boolean {
    return this.platform === PlatformType.ELECTRON || this.platform === PlatformType.MAUI;
  }

  invokePlatformMethod<T>(method: PlatformMethod, ...args: any): Observable<T> {
    throw new Error('Not Implemented');
  }

  /**
   *
   * @param filePath The relative filepath to the file to write. Forward slashes are recommended.
   * @param fileBuffer A file buffer to write to the specified path.
   */
  writeFile(filePath: string, fileBuffer: Uint8Array): Observable<boolean> {
    throw new Error('Not Implemented');
  }

  /**
   *
   * @param filePath The relative filepath to the file to read. Forward slashes are recommended.
   */
  readFile(filePath: string): Observable<Uint8Array> {
    throw new Error('Not Implemented');
  }

  /**
   *
   * @param filePath The relative filepath to the file/folder to verify. Forward slashes are recommended.
   */
  checkExists(filePath: string): Observable<boolean> {
    throw new Error('Not Implemented');
  }

  /**
   *
   * @param filePath The relative filepath to the file to remove. Forward slashes are recommended.
   */
  removeFile(filePath: string): Observable<boolean> {
    throw new Error('Not Implemented');
  }

  /**
   *
   * @param folderPath The relative path to the folder to create. Forward slashes are recommended.
   */
  createDirectory(folderPath: string): Observable<boolean> {
    throw new Error('Not Implemented');
  }

  /**
   *
   * @param folderPath The relative path to the folder to recursively delete. Forward slashes are recommended.
   */
  removeDirectory(folderPath: string): Observable<boolean> {
    throw new Error('Not Implemented');
  }

  /**
   * @param started The status of the background session.
   */
  receiveBackgroundSessionStarted(started: boolean): void {
    throw new Error('Not Implemented');
  }

  /**
   * @param content The content of the initial index.
   */
  receiveInitialIndexDownloadStatus(content: Uint8Array): void {
    throw new Error('Not Implemented');
  }

  /**
   * 
   * @param numCalls The number of calls completed.
   */
  receiveInitialZipDownloadStatus(numCalls: number): void {
    throw new Error('Not Implemented');
  }

  /**
   * 
   * @param fileName The file name of the download file.
   * @param isComplete Indicates whether the download is complete.
   */
  receiveDownloadFileStatus(fileName: string, isComplete: boolean): void { 
    throw new Error('Not Implemented');
  }
}
